import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Clock, Facebook, Instagram } from 'lucide-react';
import { companyInfo } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Footer = () => {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/categories`);
        if (response.data?.length > 0) {
          setCategories(response.data.slice(0, 4)); // Apenas 4 primeiras
        }
      } catch (error) {
        console.log('Erro ao carregar categorias');
      }
    };
    fetchCategories();
  }, []);

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-gray-950 text-gray-300">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <h3 className="text-xl font-bold text-white mb-4">Casa das Ferramentas</h3>
            <p className="text-sm leading-relaxed mb-4">
              {companyInfo.description}
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.facebook.com/casadasferramentascanaa"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 p-2 rounded-full hover:bg-blue-700 transition-colors"
              >
                <Facebook size={18} />
              </a>
              <a
                href="https://www.instagram.com/casadasferramentascanaa/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-pink-600 p-2 rounded-full hover:bg-pink-700 transition-colors"
              >
                <Instagram size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold text-white mb-4">Links Rápidos</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="hover:text-red-500 transition-colors">
                  Início
                </Link>
              </li>
              <li>
                <Link to="/catalogo" className="hover:text-red-500 transition-colors">
                  Catálogo
                </Link>
              </li>
              <li>
                <Link to="/marcas" className="hover:text-red-500 transition-colors">
                  Marcas
                </Link>
              </li>
              <li>
                <Link to="/sobre" className="hover:text-red-500 transition-colors">
                  Sobre Nós
                </Link>
              </li>
              <li>
                <Link to="/contato" className="hover:text-red-500 transition-colors">
                  Contato
                </Link>
              </li>
              {/* Link discreto para admin */}
              <li>
                <Link to="/admin-painel-gestao" className="text-gray-800 hover:text-gray-700 transition-colors text-xs opacity-30">
                  .
                </Link>
              </li>
            </ul>
          </div>

          {/* Categorias */}
          <div>
            <h4 className="text-lg font-bold text-white mb-4">Categorias</h4>
            <ul className="space-y-2">
              {categories.length > 0 ? (
                <>
                  {categories.map((cat) => (
                    <li key={cat.id}>
                      <Link 
                        to={`/catalogo?categoria=${cat.id}`} 
                        className="hover:text-red-500 transition-colors"
                      >
                        {cat.name}
                      </Link>
                    </li>
                  ))}
                  <li>
                    <Link 
                      to="/catalogo" 
                      className="text-red-500 hover:text-red-400 transition-colors font-medium"
                    >
                      Ver mais →
                    </Link>
                  </li>
                </>
              ) : (
                <li>
                  <Link to="/catalogo" className="hover:text-red-500 transition-colors">
                    Ver Catálogo
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold text-white mb-4">Contato</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <MapPin size={18} className="text-red-500 mt-1 flex-shrink-0" />
                <div className="text-sm">
                  <p>{companyInfo.address}</p>
                  <p>{companyInfo.city}</p>
                  <p>CEP {companyInfo.zipCode}</p>
                </div>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={18} className="text-red-500 flex-shrink-0" />
                <a href={`mailto:${companyInfo.email}`} className="text-sm hover:text-red-500 transition-colors">
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Clock size={18} className="text-red-500 mt-1 flex-shrink-0" />
                <span className="text-sm">
                  {companyInfo.hours}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
            <p className="text-gray-400 text-center md:text-left">© {new Date().getFullYear()} Casa das Ferramentas. Tradição em Canaã dos Carajás.</p>
            
            {/* GF Comunicação - Simples */}
            <a 
              href="https://instagram.com/gfcomunicacaoo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-gray-500 hover:text-teal-400 transition-colors"
            >
              <span className="text-xs">Desenvolvido por</span>
              <span className="font-semibold">GF Comunicação</span>
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
